import { useState, useEffect, useCallback, useRef } from "react";
import { Input } from "@/components/ui/input";
import { formatNumber, parseNumericLike } from "@/lib/numberFormatting";

interface NumericInputProps {
  value: number | string | undefined;
  onChange: (rawValue: string) => void;
  className?: string;
  placeholder?: string;
  autoFocus?: boolean;
}

/**
 * Controlled numeric input that keeps a LOCAL string state while the user types,
 * and only propagates the value upstream on blur or Enter.
 * This prevents the classic React issue where typing "123," or "0.0" gets
 * immediately converted back to a number, making decimal editing impossible.
 */
const NumericInput = ({ value, onChange, className, placeholder, autoFocus }: NumericInputProps) => {
  const [localValue, setLocalValue] = useState(() => formatForDisplay(value));
  const isFocusedRef = useRef(false);
  const lastCommittedRef = useRef(localValue);

  // Sync from parent when not focused (e.g. recalc from parent)
  useEffect(() => {
    if (!isFocusedRef.current) {
      const display = formatForDisplay(value);
      setLocalValue(display);
      lastCommittedRef.current = display;
    }
  }, [value]);

  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
  }, []);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    isFocusedRef.current = false;
    const currentVal = e.currentTarget.value;
    // Only commit if value actually changed — prevents unnecessary re-renders
    // that destroy sibling components (like Select portals) mid-interaction
    if (currentVal !== lastCommittedRef.current) {
      lastCommittedRef.current = currentVal;
      // Defer to let any concurrent click events (e.g. Select open) process first
      queueMicrotask(() => onChange(currentVal));
    }
  }, [onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        const val = (e.target as HTMLInputElement).value;
        lastCommittedRef.current = val;
        onChange(val);
        (e.target as HTMLInputElement).blur();
      }
    },
    [onChange],
  );

  return (
    <Input
      type="text"
      inputMode="decimal"
      value={localValue}
      onChange={(e) => {
        setLocalValue(e.target.value);
      }}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={className}
      placeholder={placeholder}
      autoFocus={autoFocus}
    />
  );
};

function formatForDisplay(value: number | string | undefined): string {
  if (value === undefined || value === null || value === "") return "0";
  if (typeof value === "number" && Number.isFinite(value)) {
    return formatNumber(value, { decimals: 2, minimumDecimals: 0 });
  }
  if (typeof value === "string") {
    // If the string already contains non-digit characters (comma/dot), keep the user's raw
    // input while focused; the blur handler will normalize and reformat it.
    if (/[^\d\s.,\-+]/.test(value)) return value;
    const n = parseNumericLike(value);
    return Number.isFinite(n) ? formatNumber(n, { decimals: 2, minimumDecimals: 0 }) : value;
  }
  return "0";
}

export default NumericInput;
